import * as React from 'react';
import { Container, Modal, ModalBody, Table } from 'reactstrap';

import * as AidsImportApi from '../../api/aidsImport';
import * as AdditionsImportApi from '../../api/additionsImport';
import * as AidsAJPISImportApi from '../../api/aidsAJPISImport';
import * as AdditionsAJPISImportApi from '../../api/additionsAJPISImport';
import * as TemplatesApi from '../../api/templates';

import Localization from '../../config/localization';
import { IMPORT_GET_PARTNER_INTERVAL, IMPORT_STATUS_COMPLETED, IMPORT_STATUS_STARTED } from '../../constants/imports';

import BackButton from '../../components/BackButton';
import CustomFile from '../../components/File';
import FileUpload from '../../components/FileUpload';
import Loader from '../../components/Loader';
import CheckBoxSvg from '../../components/Svg/checkBox';
import CloseSvg from '../../components/Svg/close';
import InfoSvg from '../../components/Svg/info';
import OkaySvg from '../../components/Svg/okay';
import StopSvg from '../../components/Svg/stop';
import WarningSvg from '../../components/Svg/warning';
import Tooltip from '../../components/Tooltip';

import { GetAddress } from '../../lib/address';
import Notifications from '../../lib/notifications';
import { DownloadFile, GetName, StrWithDefaultValue, ToFloat, ToNumber } from '../../lib/utils';

import './FileImport.scss';
import { fileExtension, fileExtensionTemplate } from '../../constants/file';
import FileDownload from '../../components/FileDownload';

class FileImport extends React.Component<FileImport.IProps, FileImport.IState> {
  static defaultProps = { renderTips: true };
  state: FileImport.IState = {
    loading: false,
    loadingMessage: '',

    formWasSubmitted: false,
    uploadPercent: 0,
    validatePercent: 0,

    id: 0,
    confirmImport: false,
    file: undefined,
    importCorrectDone: false,
    importDone: false,
    importEnd: 0,
    importErrors: [],
    importModify: 0,
    importNew: 0,
    invalidFile: false,
    step: 1
  };

  componentDidMount() {
    return this._doValidateFile();
  }

  renderErrors = (errors: string) => {
    if (!errors || !errors.length) {
      return null;
    }

    return errors.split(',').map(error => {
      return (
        <div key={error} className="error d-flex">
          <p className="text-truncate">{Localization.Errors[error as keyof Error.IApiError]}</p>

          <Tooltip message={Localization.ErrorsTips[error as keyof Error.IErrorTips]}>
            <InfoSvg className="mx-3 my-1" />
          </Tooltip>
        </div>
      );
    });
  };

  renderStep1 = () => {
    if (this.state.step !== 1) {
      return null;
    }

    return (
      <FileUpload
        FileChange={this._fileChange}
        RemoveFile={this._removeFile}
        DoUploadFile={this._doUploadFile}
        invalidFile={this.state.invalidFile}
        file={this.state.file}
        errorFeedback={Localization.fileImportStep1FileUploadError}
        dragMessage={Localization.fileImportStep1FileUploadDrag}
        deleteButton={Localization.fileImportStep1DeleteFile}
        uploadButton={Localization.fileImportStep1UploadButton}
      />
    );
  };

  renderStep2 = () => {
    if (this.state.step !== 2 || !this.state.file) {
      return null;
    }

    return (
      <div className="mt-5 mb-3">
        <CustomFile
          fileName={this.state.file.name}
          fileSize={this.state.file.size}
          progressMessage={this._getMessage()}
          progressColor={!(this.state.validatePercent > 0) ? 'eon-red' : 'eon-turquoise'}
          progressValue={this.state.validatePercent > 0 ? this.state.validatePercent : this.state.uploadPercent}
        />

        <div className="mt-4 w-25 pr-4 d-flex">
          <button className={`mx-auto eon-button bg-eon-red eon-white`} onClick={this._removeFile}>
            <span className="px-4">{Localization.fileImportStep2QuitButton}</span>
          </button>
        </div>
      </div>
    );
  };

  renderOk = (showOnTotal = false) => {
    if (!this.state.importCorrectDone) {
      return null;
    } else if (showOnTotal && (this.state.importErrors && this.state.importErrors.length)) {
      return null;
    }

    return (
      <div className="position-absolute text-nowrap success-import d-inline-flex align-self-end">
        <OkaySvg className="m-auto h-100" />
        <p className="ml-1 mb-0 h-100">{Localization.fileImportStep3CorrectRecordsSuccessImport}</p>
      </div>
    );
  };

  renderImportTotal = () => {
    return (
      <div className="d-flex justify-content-between position-relative">
        <p className="eon-title eon-darkgrey strong text-nowrap mr-auto">{Localization.fileImportStep3TotalLabel}</p>
        <p
          className="eon-title eon-darkgrey strong text-nowrap">{this._countCorrectImport() + this._countErrorImport()}</p>
        {this.renderOk(!!(this.state.importErrors && this.state.importErrors.length))}
      </div>
    );
  };

  renderImportResult = () => {
    return (
      <div>
        <div className="d-flex mt-3">
          <StopSvg className="m-2" />
          <div className="d-flex justify-content-between my-auto w-100">
            <p className="eon-title eon-middle-grey text-nowrap">{Localization.fileImportStep3NewLabel}</p>
            <p className="eon-title eon-middle-grey text-nowrap">{this.state.importNew}</p>
          </div>
        </div>
        <div className="d-flex">
          <StopSvg className="m-2" />
          <div className="d-flex justify-content-between my-auto w-100">
            <p className="eon-title eon-middle-grey text-nowrap">{Localization.fileImportStep3ChangedLabel}</p>
            <p className="eon-title eon-middle-grey text-nowrap">{this.state.importModify}</p>
          </div>
        </div>
        <div className="d-flex">
          <StopSvg className="m-2" />
          <div className="d-flex justify-content-between my-auto w-100">
            <p className="eon-title eon-middle-grey text-nowrap">{Localization.fileImportStep3StopLabel}</p>
            <p className="eon-title eon-middle-grey text-nowrap">{this.state.importEnd}</p>
          </div>
        </div>
      </div>
    );
  };

  renderStep3Ok = () => {
    return (
      <div className="mt-5 mb-3 position-relative">
        <div className="results-container w-100">
          {this.renderImportTotal()}
          {this.renderImportResult()}
        </div>
        <div className="mt-4 w-50 pr-4 d-flex">
          <button
            className="mr-5 eon-button bg-eon-red eon-white"
            onClick={this._confirmImport}
            disabled={this.state.importCorrectDone || !this.state.importDone}
          >
            <span className="px-4">{Localization.fileImportStep3ImportButton}</span>
          </button>
          <button className="eon-button bg-eon-white eon-middle-grey ml-5 mr-auto c-pointer"
                  onClick={this._deleteImport}>
            <CloseSvg className="my-auto mx-1 align-baseline" />
            <span className="eon-middle-grey underline eon-copy">{Localization.fileImportStep3QuitButton}</span>
          </button>
        </div>
      </div>
    );
  };

  renderStep3Error = () => {
    return (
      <div className="mt-5 mb-3 position-relative">
        <div className="results-container w-100">
          <div className="ml-4 pl-3">{this.renderImportTotal()}</div>
          <div className="mt-3 d-flex position-relative">
            <CheckBoxSvg className="m-0 mr-2" />
            <p
              className="eon-title eon-darkgrey text-nowrap ml-0 mr-auto">{Localization.fileImportStep3CorrectRecords}</p>
            <p className="eon-title eon-middle-grey text-nowrap my-auto">{this._countCorrectImport()}</p>
            {this.renderOk()}
          </div>
          <div className="ml-4 pl-1">{this.renderImportResult()}</div>
          <div className="eon-red d-flex mt-2">
            <WarningSvg className="m-0 mr-2" />
            <p className="eon-title text-nowrap ml-0 mr-auto">{Localization.fileImportStep3IncorrectRecords}</p>
            <p className="eon-title text-nowrap my-auto">{this._countErrorImport()}</p>
          </div>
        </div>
        <div className="mt-4 mb-5 w-55 d-flex justify-content-between">
          <button
            className="eon-button bg-eon-white"
            onClick={this._confirmImport}
            disabled={this.state.importCorrectDone || !this._countCorrectImport()}
          >
            <span
              className="px-2 eon-abstract eon-grey-67 text-nowrap">{Localization.fileImportStep3ImportCorrectRecordsButton}</span>
          </button>
          <button className="eon-button bg-eon-red eon-white" onClick={this._removeFile}>
            <span className="px-4 text-nowrap">{Localization.fileImportStep3ImportNewFileButton}</span>
          </button>
        </div>
        {this.renderErrorList()}
      </div>
    );
  };

  renderTableBody = () => {
    return this.state.importErrors.map((aid, index) => {
      return (
        <tr key={aid.id || index}>
          <td>
            <p className={`eon-title ${this._containError('', aid.errors)}`}>{StrWithDefaultValue(aid.codAbonat)}</p>
            <p className={`eon-title mt-1 ${this._containError('', aid.errors)}`}>
              {StrWithDefaultValue(aid.punctConsum === null ?
                GetName({ firstName: aid.prenumeTitular, lastName: aid.numeTitular })
                : GetName({ firstName: aid.prenumeBeneficiar, lastName: aid.numeBeneficiar }))}
            </p>
            <p
              className={`eon-title mt-1 ${this._containError('1025,1041', aid.errors)}`}>{StrWithDefaultValue(aid.cnpBeneficiar)}</p>
          </td>
          <td>
            <p
              className={`eon-title ${this._containError('1028', aid.errors)}`}>{StrWithDefaultValue(aid.nrDispozitie)}</p>
            <p
              className={`eon-title mt-1 ${this._containError('1016', aid.errors)}`}>{`${ToNumber(aid.ajutorStat)} lei`}</p>
            <p className={`eon-title mt-1 ${this._containError('1017', aid.errors)}`}>
              {StrWithDefaultValue(this._getMonth(aid.lunaAjutor))}
            </p>
          </td>
          <td>
            <p className={`eon-title ${this._containError('', aid.errors)}`}>
              {StrWithDefaultValue(
                GetAddress({
                  apartment: aid.ap,
                  building: aid.bloc,
                  streetName: aid.strada,
                  streetNumber: aid.numar
                } as Address.IAddress)
              )}
            </p>
            <p
              className={`eon-title mt-1 ${this._containError('', aid.errors)}`}>{StrWithDefaultValue(aid.punctConsum)}</p>
          </td>
          <td>
            <div className="eon-title eon-middle-grey mt-1">{this.renderErrors(aid.errors)}</div>
          </td>
        </tr>
      );
    });
  };

  renderErrorList = () => {
    return (
      <div className="mt-5 pt-4 hr">
        <div className="d-flex justify-content-between">
          <h4 className="eon-title eon-middle-grey force-wrap">{Localization.fileImportStep3DetectedErrors}</h4>
          <button className="eon-button bg-eon-turquoise eon-white" onClick={this._getErrorFile}>
            <span className="px-4 text-nowrap">{Localization.fileImportStep3ErrorExportButton}</span>
          </button>
        </div>
        <Table className="my-4 text-left bg-eon-white">
          <thead>
          <tr>
            <th scope="col" className="align-top">
              <p className="eon-title eon-red">{Localization.fileImportStep3TableHeadConsumerCode}</p>
              <p className="eon-title eon-red mt-1">{Localization.fileImportStep3TableHeadBeneficiary}</p>
              <p
                className="eon-title eon-red mt-1">{Localization.fileImportStep3TableHeadBeneficiaryPersonalIdentificationNumber}</p>
            </th>
            <th scope="col" className="align-top">
              <p className="eon-title eon-red">{Localization.fileImportStep3TableHeadApprovalDocumentNo}</p>
              <p className="eon-title eon-red mt-1">{Localization.fileImportStep3TableHeadStateBudgetAmount}</p>
              <p className="eon-title eon-red mt-1">{Localization.fileImportStep3TableHeadAidMonth}</p>
            </th>
            <th scope="col" className="align-top">
              <p className="eon-title eon-red">{Localization.fileImportStep3TableHeadConsumptionPointAddress}</p>
              <p className="eon-title eon-red mt-1">{Localization.fileImportStep3TableHeadConsumptionPointCode}</p>
            </th>
            <th scope="col" className="align-top">
              <p className="eon-title eon-red mt-1">{Localization.fileImportStep3TableHeadErrorDescription}</p>
            </th>
          </tr>
          </thead>
          <tbody>{this.renderTableBody()}</tbody>
        </Table>
      </div>
    );
  };

  renderStep3 = () => {
    if (this.state.step !== 3) {
      return null;
    } else if (this.state.importErrors && this.state.importErrors.length) {
      return this.renderStep3Error();
    }
    return this.renderStep3Ok();
  };

  renderTips = () => {
    if (!this.props.renderTips || this.state.step !== 1) {
      return null;
    }

    return (
      <>
        <FileDownload
          fileName={Localization.fileImportTemplateName}
          apiCall={TemplatesApi.GetImportTemplate}
          extension={fileExtensionTemplate}>
          {({ onClick }) => <>
            <h6 className="eon-title eon-middle-grey mt-3">{Localization.fileImportTips}</h6>
            <a className="eon-title eon-red mt-3 c-pointer" onClick={onClick}>
              {Localization.fileImportSubTipsLink}
            </a>
          </>}
        </FileDownload>

        <FileDownload fileName={Localization.fileImportNomenclatureName} apiCall={TemplatesApi.GetNomenclature}>
          {({ onClick }) => <>
            <a className="eon-title eon-red mt-3 c-pointer" onClick={onClick}>
              {Localization.fileImportNomenclatureTipsLink}
            </a>
          </>}
        </FileDownload>
      </>
    );
  };

  renderModalConfirmImportTitle = () => {
    const count = this._countCorrectImport();

    if (count === 1) {
      return Localization.fileImportStep3ModalTitleOne;
    } else if (count < 20) {
      return Localization.formatString(Localization.fileImportStep3ModalTitleLessThen20, count);
    }

    return Localization.formatString(Localization.fileImportStep3ModalTitle, count);
  };

  render() {
    return (
      <Container className="file-import px-5 py-4 my-4">
        <BackButton />

        <Loader loading={this.state.loading} loadingMessage={this.state.loadingMessage} />

        <h4 className="mt-5 eon-title eon-red force-wrap">{this.props.title}</h4>
        {this.renderTips()}

        {this.renderStep1()}
        {this.renderStep2()}
        {this.renderStep3()}

        <Modal isOpen={this.state.confirmImport} centered toggle={this._confirmImport}>
          <ModalBody>
            <h4 className="eon-title eon-darkgrey m-3">{this.renderModalConfirmImportTitle()}</h4>
            <div className="p-3 mt-5 w-100 d-inline-flex justify-content-between">
              <button className="eon-button bg-eon-red eon-white ml-4" onClick={this._finalizateImport}>
                <span className="px-3">{Localization.fileImportStep3ModalImport}</span>
              </button>
              <button className="eon-button bg-eon-white eon-middle-grey ml-auto c-pointer"
                      onClick={this._confirmImport}>
                <CloseSvg className="my-auto mx-1" />
                <span className="eon-middle-grey underline eon-copy">{Localization.fileImportStep3ModalExit}</span>
              </button>
            </div>
          </ModalBody>
        </Modal>
      </Container>
    );
  }

  _doUploadFile = async (event?: React.MouseEvent<HTMLButtonElement>) => {
    try {
      if (event) {
        event.preventDefault();
      }

      if (!this.state.file) {
        return this.setState({ invalidFile: true, step: 1 });
      }

      await this.setState({ step: 2, importDeleted: false });

      const formData = new FormData();

      formData.append('file', this.state.file);

      await this.props.api.UploadImportFile(formData, {
        onUploadProgress: (progress: { loaded: number, total: number }) => {
          const value = Math.round((progress.loaded * 100) / progress.total);

          return this.setState({ uploadPercent: ToFloat(value) }, () => this.forceUpdate());
        }
      });

      return this._doValidateFile();
    } catch (error) {
      Notifications.error(error && error.message);

      return this.setState({ step: 1 });
    }
  };

  _doValidateFile = async () => {
    try {
      if (this.state.importDeleted || this.state.loading) {
        return undefined;
      }

      const response = await this.props.api.GetImportStatus();

      const id = this.props.getId(response);

      if (!id) {
        return this.setState({ step: 1 });
      }

      const processedRows = ToNumber(response.data && response.data.processedRows);
      const totalRows = ToNumber(response.data && response.data.totalRows);
      const status = ToNumber(response.data && response.data.status);

      const value = Math.round((processedRows * 100) / totalRows);

      const file =
        response.data.fileName || response.data.fileSize
          ? ({ name: response.data.fileName, size: response.data.fileSize } as File)
          : undefined;

      await this.setState({ file, step: 2, validatePercent: ToFloat(value), id });

      if (status === IMPORT_STATUS_STARTED) {
        return setTimeout(this._doValidateFile, IMPORT_GET_PARTNER_INTERVAL);
      } else if (status !== IMPORT_STATUS_COMPLETED) {
        await this.setState({
          id: 0,
          confirmImport: false,
          file: undefined,
          importCorrectDone: false,
          importEnd: 0,
          importErrors: [],
          importModify: 0,
          importNew: 0,
          invalidFile: false,
          step: 1,
          uploadPercent: 0,
          validatePercent: 0
        });


        throw new Error();
      }

      return this._getImport();
    } catch (error) {
      return Notifications.error(error && error.message);
    }
  };

  _getImport = async () => {
    try {
      if (this.state.loading) {
        return undefined;
      }

      await this.setState({ loading: true });

      const response = await this.props.api.GetImport(this.state.id);

      return this.setState({
        importDone: true,
        importEnd: ToNumber(response.data.endAids),
        importErrors: this.props.getError(response),
        importModify: ToNumber(response.data.modifyAids),
        importNew: ToNumber(response.data.newAids),
        loading: false,
        step: 3
      });
    } catch (error) {
      Notifications.error(error && error.message);

      return this.setState({ loading: false });
    }
  };

  _finalizateImport = async (event: React.MouseEvent<HTMLButtonElement>) => {
    try {
      if (this.state.importCorrectDone) {
        return undefined;
      }

      event.preventDefault();

      await this.setState({ loading: true });

      const response = await this.props.api.FinalizeImport(this.state.id);

      return this.setState({
        confirmImport: false,
        importCorrectDone: response.data && response.data.success,
        loading: false
      });
    } catch (error) {
      Notifications.error(error && error.message);

      return this.setState({ loading: false });
    }
  };

  _getErrorFile = async () => {
    try {
      await this.setState({ loading: true });

      const response = await this.props.api.GetImportErrorFile(this.state.id);

      await DownloadFile(response.data, `${Localization.fileImportErrorFileName}${this.state.id}.${fileExtension}`);

      return this.setState({ loading: false });
    } catch (error) {
      Notifications.error(error && error.message);

      return this.setState({ loading: false });
    }
  };

  _deleteImport = async () => {
    await this.setState({ loading: true });

    if (this.state.id) {
      await this.props.api.DeleteImport(this.state.id);
    }

    await this.setState({
      id: 0,
      file: undefined,
      importCorrectDone: false,
      importDeleted: true,
      loading: false,
      step: 1,
      uploadPercent: 0,
      validatePercent: 0
    });

    return this._doValidateFile();
  };

  _removeFile = (event?: React.MouseEvent<HTMLParagraphElement> | React.MouseEvent<HTMLButtonElement>) => {
    if (event) {
      event.preventDefault();
    }

    return this._deleteImport();
  };

  _confirmImport = () => {
    return this.setState({ confirmImport: !this.state.confirmImport });
  };

  _fileChange = (event?: React.ChangeEvent<HTMLInputElement>) => {
    if (!event) {
      return undefined;
    }

    event.preventDefault();

    const element = event.currentTarget || event.target || {};

    const file = element.files ? element.files[0] : undefined;
    element.value = '';

    if (!file) {
      return this.setState({ invalidFile: true });
    }

    const allow = element.accept.split(',').find(accept => new RegExp(accept.trim(), 'i').test(file.name));

    if (!allow) {
      return this.setState({ invalidFile: true });
    }

    return this.setState({ file, invalidFile: false });
  };

  _getMessage = () => {
    if (this.state.uploadPercent === 100 && !(this.state.validatePercent > 0)) {
      return Localization.fileImportStep2LoadedFile;
    } else if (this.state.validatePercent === 100) {
      return Localization.fileImportStep2ValidatedFile;
    } else if (this.state.validatePercent > 0) {
      return Localization.fileImportStep2ValidateFile;
    }
    return Localization.fileImportStep2LoadingFile;
  };

  _getMonth = (month: string) => {
    return Localization.Months[ToNumber(month.slice(4))];
  };

  _containError = (error: string, errors: string) => {
    return error.split(',').find(err => new RegExp(err, 'i').test(errors)) ? 'eon-red' : 'eon-middle-grey';
  };

  _countCorrectImport = () => {
    return ToNumber(this.state.importNew) + ToNumber(this.state.importModify) + ToNumber(this.state.importEnd);
  };

  _countErrorImport = () => {
    return ToNumber(this.state.importErrors && this.state.importErrors.length);
  };
}

export const FileImportAid = (props: any) => <FileImport
  {...props}
  title={Localization.fileImportTitle}
  api={AidsImportApi}
  getId={response => response.data && response.data.aidImportId}
  getError={response => response.data && response.data.errors || []}
/>;

export const FileImportAidAJPIS = (props: any) => <FileImport
  {...props}
  title={Localization.fileImportAJPISTitle}
  renderTips={false}
  api={AidsAJPISImportApi}
  getId={response => response.data && response.data.aidImportId}
  getError={response => response.data && response.data.errors || []}
/>;

export const FileImportAddition = (props: any) => <FileImport
  {...props}
  title={Localization.fileImportAdditionTitle}
  api={AdditionsImportApi}
  getId={response => response.data && response.data.additionImportId}
  getError={response => response.data && response.data.errorsAddition || []}
/>;

export const FileImportAdditionAJPIS = (props: any) => <FileImport
  {...props}
  title={Localization.fileImportAdditionAJPISTitle}
  renderTips={false}
  api={AdditionsAJPISImportApi}
  getId={response => response.data && response.data.additionImportId}
  getError={response => response.data && response.data.errorsAddition || []}
/>;

